import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CircleNotch, CheckCircle, WarningCircle, Bell } from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';

// ── Types ─────────────────────────────────────────────────────────────────

interface NotificationHealthData {
  totalSent: number;
  delivered: number;
  failed: number;
  pending: number;
  deliveryRate: number;
  byType: Array<{ type: string; total: number; delivered: number; failed: number }>;
  byChambers: Array<{ chambersId: string; name: string; total: number; failed: number }>;
  recentFailures: Array<{
    id: string;
    chambersName: string;
    userId: string;
    userName: string;
    type: string;
    failedAt: string;
    failureReason: string | null;
    createdAt: string;
  }>;
}

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtDateTime(str: string) {
  return new Date(str).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function typeLabel(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function StatBox({ label, value, sub, colour }: {
  label: string; value: string | number; sub?: string;
  colour?: 'green' | 'red' | 'amber' | 'blue';
}) {
  const text = colour === 'green' ? 'text-plat-success-text'
    : colour === 'red' ? 'text-plat-danger-text'
    : colour === 'amber' ? 'text-plat-warning-text'
    : 'text-plat-fg';
  return (
    <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
      <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-plat-muted">{label}</div>
      <div className={`mt-2 text-[28px] font-bold leading-none ${text}`}>{value}</div>
      {sub && <div className="mt-1.5 text-xs text-plat-faint">{sub}</div>}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────

type Range = '24h' | '7d' | '30d';

const RANGE_OFFSETS: Record<Range, number> = { '24h': 1, '7d': 7, '30d': 30 };
const RANGE_LABELS: Record<Range, string> = { '24h': 'Last 24h', '7d': 'Last 7 days', '30d': 'Last 30 days' };

export default function NotificationHealthPage() {
  const [range, setRange] = useState<Range>('7d');

  const dateFrom = new Date(Date.now() - RANGE_OFFSETS[range] * 86_400_000).toISOString().slice(0, 10);
  const dateTo = new Date().toISOString().slice(0, 10);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['platform', 'notifications', 'health', range],
    queryFn: async () => {
      const res = await platformApi.get<{ data: NotificationHealthData }>(
        '/notifications/health',
        { params: { dateFrom, dateTo } },
      );
      return res.data.data;
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-plat-fg">Notification Health</h2>
          <p className="mt-0.5 text-sm text-plat-muted">
            Real-time delivery tracking — WebSocket push success rate
          </p>
        </div>
        <div className="flex gap-1 rounded-xl border border-plat-border bg-plat-bg p-1">
          {(Object.keys(RANGE_LABELS) as Range[]).map((r) => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all duration-150 ${
                range === r ? 'bg-plat-fg text-plat-surface shadow-sm' : 'text-plat-muted hover:text-plat-fg'
              }`}
            >
              {RANGE_LABELS[r]}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-plat-muted">
          <CircleNotch size={16} className="animate-spin" /> Loading health data…
        </div>
      )}

      {isError && (
        <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft px-4 py-3 text-sm text-plat-danger-text">
          {getPlatformApiErrorMessage(error)}
        </div>
      )}

      {data && (
        <>
          {/* Summary stat cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatBox
              label="Total sent"
              value={data.totalSent.toLocaleString()}
              sub={`${RANGE_LABELS[range]}`}
            />
            <StatBox
              label="Delivered (real-time)"
              value={data.delivered.toLocaleString()}
              sub={`${data.deliveryRate}% delivery rate`}
              colour={data.deliveryRate >= 80 ? 'green' : data.deliveryRate >= 60 ? 'amber' : 'red'}
            />
            <StatBox
              label="Failed (user offline)"
              value={data.failed.toLocaleString()}
              sub="Will appear on next poll"
              colour={data.failed > 0 ? 'amber' : 'green'}
            />
            <StatBox
              label="Delivery rate"
              value={`${data.deliveryRate}%`}
              sub={data.deliveryRate >= 80 ? 'Above target' : 'Below 80% target'}
              colour={data.deliveryRate >= 80 ? 'green' : data.deliveryRate >= 60 ? 'amber' : 'red'}
            />
          </div>

          {/* Delivery rate bar */}
          <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-plat-fg">WebSocket delivery rate</span>
              <span className={`text-sm font-bold ${data.deliveryRate >= 80 ? 'text-plat-success-text' : data.deliveryRate >= 60 ? 'text-plat-warning-text' : 'text-plat-danger-text'}`}>
                {data.deliveryRate}%
              </span>
            </div>
            <div className="h-2.5 overflow-hidden rounded-full bg-plat-border">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  data.deliveryRate >= 80 ? 'bg-plat-success-solid' : data.deliveryRate >= 60 ? 'bg-plat-warning-solid' : 'bg-plat-danger-solid'
                }`}
                style={{ width: `${data.deliveryRate}%` }}
              />
            </div>
            <div className="mt-2 flex justify-between text-[10px] text-plat-faint">
              <span>{data.delivered.toLocaleString()} real-time push</span>
              <span>{data.failed.toLocaleString()} offline (fallback to poll)</span>
            </div>
          </div>

          {/* By type + by chambers */}
          <div className="grid gap-5 lg:grid-cols-2">
            {/* By type */}
            <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
              <div className="mb-4 text-sm font-semibold text-plat-fg">Breakdown by type</div>
              {data.byType.length === 0 ? (
                <div className="py-8 text-center text-xs text-plat-faint">No data in period</div>
              ) : (
                <div className="space-y-2.5">
                  {data.byType.map((row) => {
                    const rate = row.total > 0 ? Math.round((row.delivered / row.total) * 100) : 0;
                    return (
                      <div key={row.type}>
                        <div className="mb-1 flex items-center justify-between text-xs">
                          <span className="flex items-center gap-2">
                            <Bell size={11} className="text-plat-muted" />
                            <span className="text-plat-secondary">{typeLabel(row.type)}</span>
                          </span>
                          <span className="text-plat-muted">
                            {row.total.toLocaleString()} sent ·{' '}
                            <span className={rate >= 80 ? 'text-plat-success-text' : 'text-plat-warning-text'}>{rate}%</span>
                          </span>
                        </div>
                        <div className="h-1.5 overflow-hidden rounded-full bg-plat-border">
                          <div
                            className={`h-full rounded-full ${rate >= 80 ? 'bg-plat-success-solid' : rate >= 60 ? 'bg-plat-warning-solid' : 'bg-plat-danger-solid'}`}
                            style={{ width: `${rate}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* By chambers */}
            <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
              <div className="mb-4 text-sm font-semibold text-plat-fg">Breakdown by chambers</div>
              {data.byChambers.length === 0 ? (
                <div className="py-8 text-center text-xs text-plat-faint">No data in period</div>
              ) : (
                <div className="divide-y divide-plat-border">
                  {data.byChambers.map((row) => {
                    const failRate = row.total > 0 ? Math.round((row.failed / row.total) * 100) : 0;
                    return (
                      <div key={row.chambersId} className="flex items-center justify-between py-2.5">
                        <span className="text-xs text-plat-secondary truncate max-w-[160px]">{row.name}</span>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-plat-muted">{row.total.toLocaleString()} sent</span>
                          {row.failed > 0 ? (
                            <span className="flex items-center gap-1 text-plat-warning-text">
                              <WarningCircle size={11} weight="fill" />
                              {row.failed} offline
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-plat-success-text">
                              <CheckCircle size={11} weight="fill" />
                              All delivered
                            </span>
                          )}
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${failRate === 0 ? 'bg-plat-success-soft text-plat-success-text' : 'bg-plat-warning-soft text-plat-warning-text'}`}>
                            {100 - failRate}% live
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Recent failures */}
          {data.recentFailures.length > 0 && (
            <div className="rounded-xl border border-plat-border bg-plat-surface">
              <div className="flex items-center justify-between border-b border-plat-border px-5 py-3">
                <span className="text-sm font-semibold text-plat-fg">Recent offline deliveries</span>
                <span className="text-xs text-plat-faint">
                  {data.recentFailures.length} notification{data.recentFailures.length !== 1 ? 's' : ''} — user was offline at send time
                </span>
              </div>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-plat-border text-[10px] font-bold uppercase tracking-wider text-plat-faint">
                    <th className="px-5 py-2.5 text-left">User</th>
                    <th className="px-5 py-2.5 text-left">Chambers</th>
                    <th className="px-5 py-2.5 text-left">Type</th>
                    <th className="px-5 py-2.5 text-left">Sent</th>
                    <th className="px-5 py-2.5 text-left">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-plat-border">
                  {data.recentFailures.map((f) => (
                    <tr key={f.id} className="text-xs">
                      <td className="px-5 py-3 text-plat-secondary">{f.userName}</td>
                      <td className="px-5 py-3 text-plat-muted">{f.chambersName}</td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-plat-border px-2 py-0.5 text-[10px] text-plat-secondary">
                          {f.type}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-plat-muted">{fmtDateTime(f.failedAt)}</td>
                      <td className="px-5 py-3">
                        <span className="rounded-full bg-plat-warning-soft px-2 py-0.5 text-[10px] text-plat-warning-text">
                          {f.failureReason ?? 'unknown'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.recentFailures.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-plat-border bg-plat-surface py-12 text-center">
              <CheckCircle size={28} weight="duotone" className="text-plat-success-text" />
              <div className="text-sm font-semibold text-plat-fg">No offline deliveries</div>
              <div className="text-xs text-plat-faint">All notifications reached users in real-time during this period.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
